// components/monitoring/t247Shared.js
// Shared theme tokens, data transforms, and tiny hand-drawn SVG chart pieces used by
// both DesktopLayout.js and MobileLayout.js, so the two stay visually and logically
// in sync instead of drifting apart.
import React from "react";

// ─── Formatting ──────────────────────────────────────────────────────────────
export const formatUptime = (uptimeString) => {
  if (!uptimeString || uptimeString === "N/A") return "N/A";
  const daysMatch = uptimeString.match(/(\d+)\s+days?/);
  const hoursMatch = uptimeString.match(/(\d+)\s+hours?/);
  const minutesMatch = uptimeString.match(/(\d+)\s+minutes?/);
  const secondsMatch = uptimeString.match(/(\d+)\s+seconds?/);
  const days = daysMatch ? parseInt(daysMatch[1]) : 0;
  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
  const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 && days === 0) parts.push(`${seconds}s`);
  return parts.length > 0 ? parts.join(" ") : "0s";
};

export const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "0.00");
export const fmt1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "0.0");
export const safeArr = (a) => (Array.isArray(a) ? a : []);

export const extractLogArray = (logs) => {
  if (!logs) return [];
  if (Array.isArray(logs)) return logs;
  if (logs.data && Array.isArray(logs.data)) return logs.data;
  if (typeof logs === "object") {
    if (logs.logs && Array.isArray(logs.logs)) return logs.logs;
    for (const key in logs) {
      if (Array.isArray(logs[key])) return logs[key];
    }
    return Object.values(logs);
  }
  return [];
};

export const extractLogText = (log) => {
  if (!log) return "No details";
  if (log.detail) return log.detail;
  if (log.message) return log.message;
  if (log.details) return log.details;
  if (log.event_type) return log.event_type;
  if (log.description) return log.description;
  return "Log entry";
};

export const extractLogTime = (log) => {
  const raw = log?.ts || log?.datetime || log?.timestamp;
  if (!raw) return "--:--";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "--:--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

export const extractLogLevel = (log) => {
  const text = `${extractLogText(log)} ${log?.level || log?.priority || ""}`.toLowerCase();
  if (/(warn|down|fail|denied|drop|error)/.test(text)) return "WARN";
  return "INFO";
};

// ─── Device data normalization (identical shape used by both layouts) ───────
export const buildDeviceData = (rawDeviceData) => {
  if (!rawDeviceData) return null;
  return {
    deviceName: rawDeviceData.deviceName || "",
    serialNumber: rawDeviceData.serialNumber || "",
    model: rawDeviceData.model || "",
    firmwareVersion: rawDeviceData.firmwareVersion || "",
    lastStatus: rawDeviceData.lastStatus || "",
    onlineAt: rawDeviceData.onlineAt ? rawDeviceData.onlineAt.replace("T", " ") : "",
    offlineAt: rawDeviceData.offlineAt ? rawDeviceData.offlineAt.replace("T", " ") : "",
    uptime: formatUptime(rawDeviceData.uptime || "N/A"),
    uptimeRaw: rawDeviceData.uptime || "N/A",
    clientCount: rawDeviceData.clientCount || "",

    vlanInterfaces: rawDeviceData.vlanInterfaces?.vlan_interfaces || rawDeviceData.vlanInterfaces || [],
    wanInterfaces: rawDeviceData.interfaces?.wan_interfaces || [],
    cellularInterfaces: rawDeviceData.interfaces?.cellular_interfaces || [],
    availableDates: rawDeviceData.availableDates || [],

    clientUsages: (() => {
      const clientData = rawDeviceData.clientUsage;
      if (!clientData || !Array.isArray(clientData)) return [];
      return clientData.map((client) => ({
        name: client.client_name || "Unknown",
        ip_address: client.ip || "N/A",
        mac_address: client.mac || "N/A",
        download: (client.rx || 0) / (1024 * 1024),
        upload: (client.tx || 0) / (1024 * 1024),
        total_usage: (client.usage || 0) / (1024 * 1024),
        vlan: client.vlan || 0,
        usage_percent: client.usage_percent || 0,
        type: client.type || "unknown",
        status: client.status,
        manufacturer: client.manufacturer || "Unknown",
      }));
    })(),

    locationMap: rawDeviceData.locationMap || [],
    starlinkData: rawDeviceData.starlinkData,

    pepVPNLogs: extractLogArray(rawDeviceData.pepVPNLogs),
    wanLogs: extractLogArray(rawDeviceData.wanLogs),
    systemLogs: extractLogArray(rawDeviceData.systemLogs),
    adminLogs: extractLogArray(rawDeviceData.adminLogs),
    dhcpLogs: extractLogArray(rawDeviceData.dhcpLogs),

    hourlyWanUsagesLogs: rawDeviceData.hourlyWanUsagesLogs || [],
    dailyWanUsagesLogs: rawDeviceData.dailyWanUsagesLogs || [],
    monthlyWanUsagesLogs: rawDeviceData.monthlyWanUsagesLogs || [],
    dailyBreakdown: rawDeviceData.dailyBreakdown || [],
    simLogs: rawDeviceData.simLogs || [],

    dataUsage: (() => {
      const usageData = rawDeviceData.dataUsage || [];
      return usageData.map((usage) => ({
        ...usage,
        download: parseFloat(usage.download || 0) / 1024,
        upload: parseFloat(usage.upload || 0) / 1024,
      }));
    })(),

    ping: rawDeviceData.ping || null,

    speedtest: rawDeviceData.speedtest
      ? {
          scanner_id: rawDeviceData.speedtest.scanner_id,
          date: rawDeviceData.speedtest.date,
          last_updated: rawDeviceData.speedtest.last_updated,
          total_tests_today: rawDeviceData.speedtest.total_tests_today || 0,
          tests: (rawDeviceData.speedtest.tests || []).map((test) => ({
            timestamp: test.timestamp,
            timestamp_human: test.timestamp_human,
            ping: test.ping,
            download: test.download,
            upload: test.upload,
            server: test.server,
          })),
        }
      : null,
  };
};

// ─── WAN rows (also returns the raw interface for the detail modal) ─────────
export const computeWanRows = (deviceData) => {
  if (!deviceData) return { rows: [], totals: { download: 0, upload: 0, total: 0 }, downCount: 0 };
  const normalizePriority = (p) => {
    if (typeof p === "number") return p;
    if (typeof p === "string" && !isNaN(parseInt(p))) return parseInt(p);
    return 99;
  };
  const combined = [...safeArr(deviceData.wanInterfaces)];
  safeArr(deviceData.cellularInterfaces).forEach((cell) => {
    const exists = combined.some((i) => i.id === cell.id || i.name === cell.name);
    if (!exists) combined.push({ ...cell, isCellular: true, type: "cellular" });
  });
  // Fallback: the backend sometimes returns no live WAN status (e.g. the
  // device was offline / the status call failed while the report was built)
  // even though usage data exists. Build placeholder interfaces from
  // dataUsage so usage is still shown, marked as "no live status".
  if (combined.length === 0 && safeArr(deviceData.dataUsage).length > 0) {
    const wanMeta = {};
    [deviceData.dailyWanUsagesLogs, deviceData.hourlyWanUsagesLogs, deviceData.monthlyWanUsagesLogs].forEach((log) => {
      safeArr(log?.wans).forEach((w) => {
        if (w && w.id !== undefined && w.id !== 0 && !wanMeta[w.id]) wanMeta[w.id] = w;
      });
    });
    safeArr(deviceData.dataUsage).forEach((u) => {
      if (u.wan_id === undefined) return;
      const meta = wanMeta[u.wan_id] || {};
      const vtype = meta.virtual_type || u.wan_type || "";
      combined.push({
        id: u.wan_id,
        name: u.wan_name || meta.name || `WAN ${u.wan_id}`,
        type: u.wan_type || meta.type || "",
        virtual_type: vtype,
        isCellular: vtype === "cellular" || u.wan_type === "gobi",
        status: "No live status",
        status_led: undefined,
        priority: undefined,
        _synthesized: true,
      });
    });
  }

  combined.sort((a, b) => normalizePriority(a.priority) - normalizePriority(b.priority));

  const usageMap = {};
  safeArr(deviceData.dataUsage).forEach((u) => {
    if (u.wan_id !== undefined) {
      usageMap[u.wan_id] = { download: u.download || 0, upload: u.upload || 0, wan_name: u.wan_name || "" };
    }
  });

  const getUsage = (iface) => {
    if (iface.id !== undefined && usageMap[iface.id]) return usageMap[iface.id];
    for (const usage of Object.values(usageMap)) {
      if (usage.wan_name && iface.name && (usage.wan_name === iface.name || iface.name.includes(usage.wan_name) || usage.wan_name.includes(iface.name))) {
        return usage;
      }
    }
    return { download: 0, upload: 0 };
  };

  const totalAll = Object.values(usageMap).reduce((s, u) => s + u.download + u.upload, 0);

  const rows = combined.map((iface, idx) => {
    const usage = getUsage(iface);
    const total = usage.download + usage.upload;
    const statusRaw = (iface.status || iface.led_color || iface.ledColor || "").toString().toLowerCase();
    const isCellular = iface.isCellular === true || iface.type === "cellular" || iface.type === "gobi";
    return {
      key: iface.id ?? iface.name ?? idx,
      raw: iface,
      name: iface.name || `Interface ${idx + 1}`,
      ip: iface.ip_address || iface.ip || "—",
      priority: iface.priority ?? "—",
      isCellular,
      ledColor: iface.status_led,
      statusLabel: iface.status,
      isDown: (iface.status_led || "").toString().toLowerCase() === "red",
      synthesized: iface._synthesized === true,
      download: usage.download,
      upload: usage.upload,
      total,
      pct: totalAll > 0 ? (total / totalAll) * 100 : 0,
    };
  });

  const totals = rows.reduce((acc, r) => ({ download: acc.download + r.download, upload: acc.upload + r.upload, total: acc.total + r.total }), {
    download: 0,
    upload: 0,
    total: 0,
  });
  const downCount = rows.filter((r) => r.isDown).length;

  return { rows, totals, downCount };
};

// ─── VLAN rows (each row carries its member clients for the detail modal) ──
export const computeVlanRows = (deviceData) => {
  if (!deviceData) return [];
  const nameMap = {};
  safeArr(deviceData.vlanInterfaces).forEach((v) => {
    if (v.vlan_id !== undefined && v.vlan_id !== null) nameMap[v.vlan_id] = v.name || "";
  });
  const map = {};
  safeArr(deviceData.clientUsages).forEach((c) => {
    const vlanId = c.vlan ?? 0;
    if (!map[vlanId]) {
      const vlanInfo = safeArr(deviceData.vlanInterfaces).find((v) => v.vlan_id === vlanId) || null;
      map[vlanId] = {
        vlan: vlanId,
        name: nameMap[vlanId] || (vlanId === 0 ? "Native" : `VLAN ${vlanId}`),
        download: 0,
        upload: 0,
        total: 0,
        clients: 0,
        clientList: [],
        vlanInfo,
      };
    }
    map[vlanId].download += (c.download || 0) / 1024;
    map[vlanId].upload += (c.upload || 0) / 1024;
    map[vlanId].total += (c.total_usage || 0) / 1024;
    map[vlanId].clients += 1;
    map[vlanId].clientList.push(c);
  });
  const rows = Object.values(map).sort((a, b) => b.total - a.total);
  const maxTotal = Math.max(...rows.map((r) => r.total), 0.0001);
  return rows.map((r) => ({ ...r, pct: (r.total / maxTotal) * 100 }));
};

export const computeTopTalkers = (deviceData, limit = 5) => {
  if (!deviceData) return [];
  const rows = [...safeArr(deviceData.clientUsages)]
    .sort((a, b) => (b.total_usage || 0) - (a.total_usage || 0))
    .slice(0, limit)
    .map((c) => ({ ...c, totalGB: (c.total_usage || 0) / 1024 }));
  const max = Math.max(...rows.map((r) => r.totalGB), 0.0001);
  return rows.map((r) => ({ ...r, pct: (r.totalGB / max) * 100 }));
};

export const computeThroughput = (deviceData, period) => {
  if (!deviceData) return { down: [], up: [], totalDown: 0, totalUp: 0 };
  if (period === "daily") {
    const usages = safeArr(deviceData.hourlyWanUsagesLogs?.usages);
    return {
      down: usages.map((u) => (u.down || 0) / 1024),
      up: usages.map((u) => (u.up || 0) / 1024),
      totalDown: usages.reduce((s, u) => s + (u.down || 0), 0) / 1024,
      totalUp: usages.reduce((s, u) => s + (u.up || 0), 0) / 1024,
    };
  }
  const rows = [...safeArr(deviceData.dailyBreakdown)].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  return {
    down: rows.map((r) => (r.downMB || 0) / 1024),
    up: rows.map((r) => (r.upMB || 0) / 1024),
    totalDown: rows.reduce((s, r) => s + (r.downMB || 0), 0) / 1024,
    totalUp: rows.reduce((s, r) => s + (r.upMB || 0), 0) / 1024,
  };
};

export const computeSpeedtestStats = (deviceData) => {
  const tests = safeArr(deviceData?.speedtest?.tests);
  if (!tests.length) return null;
  const pings = tests.map((t) => t.ping || 0);
  const downs = tests.map((t) => t.download || 0);
  const ups = tests.map((t) => t.upload || 0);
  const avgPing = pings.reduce((a, b) => a + b, 0) / pings.length;
  const variance = pings.reduce((s, p) => s + Math.pow(p - avgPing, 2), 0) / pings.length;
  return { avgPing, maxDown: Math.max(...downs), maxUp: Math.max(...ups), jitter: Math.sqrt(variance), downs, ups, pings, tests };
};

export const computePingStats = (deviceData) => {
  const cycles = safeArr(deviceData?.ping?.ping_cycles);
  if (!cycles.length) return null;
  const targets = {};
  const rttSeries = [];
  let totalChecks = 0,
    totalLoss = 0;
  cycles.forEach((cycle) => {
    let cycleRtts = [];
    safeArr(cycle.results).forEach((r) => {
      totalChecks += 1;
      totalLoss += r.packet_loss || 0;
      if (!targets[r.target]) targets[r.target] = { target: r.target, checks: 0, success: 0, rtts: [], losses: [] };
      targets[r.target].checks += 1;
      if (r.success) targets[r.target].success += 1;
      if (r.rtt_avg) {
        targets[r.target].rtts.push(r.rtt_avg);
        cycleRtts.push(r.rtt_avg);
      }
      targets[r.target].losses.push(r.packet_loss || 0);
    });
    if (cycleRtts.length) rttSeries.push(cycleRtts.reduce((a, b) => a + b, 0) / cycleRtts.length);
  });
  const targetRows = Object.values(targets).map((t) => ({
    target: t.target,
    checks: t.checks,
    successRate: t.checks ? (t.success / t.checks) * 100 : 0,
    avgLoss: t.losses.length ? t.losses.reduce((a, b) => a + b, 0) / t.losses.length : 0,
    avgRtt: t.rtts.length ? t.rtts.reduce((a, b) => a + b, 0) / t.rtts.length : null,
    minRtt: t.rtts.length ? Math.min(...t.rtts) : null,
    maxRtt: t.rtts.length ? Math.max(...t.rtts) : null,
  }));
  return {
    targets: targetRows,
    avgPacketLoss: totalChecks ? totalLoss / totalChecks : null,
    rttSeries,
    totalChecks,
  };
};

// ─── Tiny hand-drawn SVG chart pieces (mockup style — no chart library) ─────
export const buildLinePoints = (values, w, h, padTop = 6, padBottom = 6) => {
  const vals = values.length ? values : [0];
  const max = Math.max(...vals, 0.0001);
  const step = vals.length > 1 ? w / (vals.length - 1) : 0;
  return vals
    .map((v, i) => {
      const x = i * step;
      const y = h - padBottom - (v / max) * (h - padTop - padBottom);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};

export const AreaSpark = ({ values, width = 480, height = 150, color = "#88F4EB", fillOpacity = 0.16 }) => {
  const points = buildLinePoints(values, width, height, 10, 8);
  const areaPath = points ? `M0,${height} L${points} L${width},${height} Z` : "";
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <line x1="0" y1={height * 0.15} x2={width} y2={height * 0.15} stroke="rgba(255,255,255,.07)" />
      <line x1="0" y1={height * 0.45} x2={width} y2={height * 0.45} stroke="rgba(255,255,255,.07)" />
      <line x1="0" y1={height - 8} x2={width} y2={height - 8} stroke="rgba(255,255,255,.2)" />
      {areaPath && <path d={areaPath} fill={color} opacity={fillOpacity} />}
      {points && <polyline points={points} fill="none" stroke={color} strokeWidth="1.8" />}
    </svg>
  );
};

export const DualAreaSpark = ({ down, up, width = 480, height = 150 }) => {
  const downPts = buildLinePoints(down, width, height, 10, 8);
  const upPts = buildLinePoints(up, width, height, 10, 8);
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <line x1="0" y1={height * 0.15} x2={width} y2={height * 0.15} stroke="rgba(255,255,255,.07)" />
      <line x1="0" y1={height * 0.45} x2={width} y2={height * 0.45} stroke="rgba(255,255,255,.07)" />
      <line x1="0" y1={height - 8} x2={width} y2={height - 8} stroke="rgba(255,255,255,.2)" />
      {downPts && <path d={`M0,${height} L${downPts} L${width},${height} Z`} fill="#D7D7CC" opacity="0.16" />}
      {downPts && <polyline points={downPts} fill="none" stroke="#D7D7CC" strokeWidth="1.8" />}
      {upPts && <polyline points={upPts} fill="none" stroke="#88F4EB" strokeWidth="1.8" />}
    </svg>
  );
};

export const MiniBar = ({ pct, color = "#88F4EB", track = "rgba(255,255,255,.13)" }) => (
  <div style={{ flex: 1, height: 8, background: track }}>
    <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: 8, background: color }} />
  </div>
);

// Single-value ring gauge (e.g. signal quality %) — one filled arc, brand turquoise by default.
export const RingGauge = ({ percent = 0, size = 86, stroke = 10, color = "#88F4EB", trackColor = "rgba(255,255,255,.13)" }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, percent || 0));
  const dash = (pct / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", flex: "none" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        strokeLinecap="butt"
      />
    </svg>
  );
};

// Bar-chart sparkline (e.g. daily usage) — most recent bar highlighted turquoise.
export const BarSpark = ({ values, width = 340, height = 74, barColor = "#D7D7CC", highlightColor = "#88F4EB", highlightLast = 1 }) => {
  const vals = values && values.length ? values : [0];
  const max = Math.max(...vals, 0.0001);
  const gap = 3;
  const barW = Math.max(2, width / vals.length - gap);
  const baseline = height - 8;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <line x1="0" y1={baseline} x2={width} y2={baseline} stroke="rgba(255,255,255,.2)" />
      {vals.map((v, i) => {
        const h = Math.max(1, (v / max) * (baseline - 6));
        const x = i * (barW + gap);
        const isHighlight = i >= vals.length - highlightLast;
        return <rect key={i} x={x} y={baseline - h} width={barW} height={h} fill={isHighlight ? highlightColor : barColor} />;
      })}
    </svg>
  );
};

// Donut chart for WAN usage distribution — one ring per metric, one arc per WAN.
const DONUT_COLORS = ["#88F4EB", "#CAFB6D", "#D7D7CC", "#5FD9CF", "#AEDE55"];
export const DonutChart = ({ segments, size = 140, stroke = 16, label, unit = "GB" }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth={stroke} />
        {total > 0 &&
          segments.map((seg, i) => {
            const frac = seg.value / total;
            const dash = frac * c;
            const el = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={seg.color || DONUT_COLORS[i % DONUT_COLORS.length]}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += dash;
            return el;
          })}
        <text x={size / 2} y={size / 2 - 4} textAnchor="middle" fill="#FFFFFF" fontSize="18" fontWeight="700" fontFamily="'DM Mono',monospace">
          {fmt1(total)}
        </text>
        <text x={size / 2} y={size / 2 + 13} textAnchor="middle" fill="rgba(255,255,255,.52)" fontSize="9" fontFamily="'DM Mono',monospace" letterSpacing="1">
          {unit}
        </text>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        {label && <div style={{ font: "400 9.5px 'DM Mono',monospace", letterSpacing: "0.1em", color: "rgba(255,255,255,.52)", marginBottom: 8 }}>{label}</div>}
        {segments.map((seg, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 11.5, fontFamily: "'DM Sans',sans-serif" }}>
            <span style={{ width: 8, height: 8, background: seg.color || DONUT_COLORS[i % DONUT_COLORS.length], flex: "none" }} />
            <span style={{ flex: 1, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{seg.name}</span>
            <span style={{ fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,.72)" }}>{fmt2(seg.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Theme CSS shared by both layouts ────────────────────────────────────────
export const T247_THEME_CSS = `
  @import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,700;1,400&family=DM+Mono:wght@400;500&display=swap");

  .t247 {
    --navy-900: #0B1618;
    --navy-800: #112023;
    --navy-700: #1A2E32;
    --navy-600: #16292C;
    --turquoise: #88F4EB;
    --neon: #CAFB6D;
    --neutral: #D7D7CC;
    --line: rgba(255, 255, 255, 0.1);
    --line-soft: rgba(255, 255, 255, 0.07);
    --text-muted: rgba(255, 255, 255, 0.52);
    --text-dim: rgba(255, 255, 255, 0.7);
    --font-sans: "DM Sans", "Helvetica Neue", Helvetica, sans-serif;
    --font-mono: "DM Mono", ui-monospace, "SF Mono", Menlo, monospace;

    background: var(--navy-900);
    color: #fff;
    font-family: var(--font-sans);
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;

    /* ── Generic var(--color-x) compatibility layer ──────────────────
       A lot of the app's existing components/CSS already theme via this
       naming convention (var(--color-primary), var(--color-background),
       etc.) rather than the .t247-* classes above. Defining them here
       once means any page that wraps its root in .t247 and imports this
       stylesheet inherits the full dark palette automatically, without
       needing its own compatibility block. */
    --color-primary: var(--turquoise);
    --color-secondary: var(--neon);
    --color-accent: var(--navy-700);
    --color-background: var(--navy-800);
    --color-surface: var(--navy-700);
    --color-card: var(--navy-800);
    --color-border: var(--line);
    --color-label: var(--text-muted);
    --color-text-primary: #fff;
    --color-text-secondary: var(--text-dim);
    --color-muted: rgba(255, 255, 255, 0.28);
    --color-download: #D7D7CC;
    --color-upload: var(--turquoise);
    --color-success: var(--turquoise);
    --color-warning: var(--neon);
    --color-error: #fff;
    --color-error-bg: rgba(255, 255, 255, 0.1);
    --color-error-border: rgba(255, 255, 255, 0.28);
    --color-danger: #fff;
    --color-starlink: var(--neon);
    --color-shadow: rgba(0, 0, 0, 0.45);
    --font-main: var(--font-sans);
    --color-disable: rgba(255, 255, 255, 0.18);
    --color-p0: var(--turquoise);
    --color-p1: var(--neon);
    --color-p2: #D7D7CC;
    --color-p3: rgba(255, 255, 255, 0.4);
  }

  .t247-eyebrow { font: 400 9.5px var(--font-mono); letter-spacing: 0.14em; color: var(--text-dim); }
  .t247-panel { background: var(--navy-800); }
  .t247-panel-header-row {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 14px 18px; border-bottom: 1px solid var(--line);
    font: 400 10px var(--font-mono); letter-spacing: 0.14em; color: var(--text-dim);
  }
  .t247-panel-header-row .sub { font: 400 9.5px var(--font-mono); letter-spacing: 0.06em; color: var(--text-muted); }
  .t247-muted-label { font: 400 10px var(--font-mono); letter-spacing: 0.1em; color: var(--color-secondary); }
  .t247-secondary-label { font: 400 10px var(--font-mono); letter-spacing: 0.1em; color: var(--text-muted); }
  .t247-empty { padding: 28px 18px; text-align: center; font: 400 11px var(--font-mono); color: var(--text-muted); }
  .t247-num { text-align: right; font: 400 13px var(--font-mono); }
  .t247-num.strong { font-weight: 700; }
  .t247-chip { padding: 2px 6px; font: 400 9px var(--font-mono); letter-spacing: 0.08em; }
  .chip-up { background: rgba(136, 244, 235, 0.16); color: var(--turquoise); }
  .chip-down { background: #fff; color: var(--navy-800); }
  .chip-cell { background: rgba(202, 251, 109, 0.16); color: var(--neon); }
  .chip-other { background: rgba(202, 251, 109, 0.16); color: var(--neon); }

  /* Modal — brand anatomy: mono title, info line, sectioned body, footer pair */
  .t247-modal-overlay {
    position: fixed; inset: 0; background: rgba(11, 22, 24, 0.72);
    display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 20px;
  }
  .t247-modal {
    background: var(--navy-800); border: 1px solid var(--line);
    width: 100%; max-width: 640px; max-height: 86vh; overflow: hidden;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 64px rgba(0,0,0,.5);
  }
  .t247-modal-header {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 16px 20px; background: var(--navy-700); border-bottom: 1px solid var(--line); flex: none;
  }
  .t247-modal-title { font: 400 11px var(--font-mono); letter-spacing: 0.1em; color: #fff; }
  .t247-modal-close {
    background: transparent; border: none; color: var(--text-dim); cursor: pointer;
    font-size: 18px; line-height: 1; padding: 4px 8px;
  }
  .t247-modal-close:hover { color: #fff; }
  .t247-modal-info {
    padding: 12px 20px; border-bottom: 1px solid var(--line); background: var(--navy-800);
    font: 400 11px var(--font-mono); color: var(--text-dim); display: flex; gap: 14px; flex-wrap: wrap; flex: none;
  }
  .t247-modal-body { padding: 0; overflow-y: auto; flex: 1; }
  .t247-modal-section-title {
    display: flex; align-items: baseline; justify-content: space-between;
    padding: 12px 20px 8px; font: 400 9.5px var(--font-mono); letter-spacing: 0.12em; color: var(--text-muted);
  }
  .t247-modal-telemetry-source { font: 400 9px var(--font-mono); letter-spacing: 0.08em; color: var(--turquoise); }
  .t247-modal-grid { display: grid; grid-template-columns: 1fr 1fr; }
  .t247-modal-cell { padding: 10px 20px; border-bottom: 1px solid var(--line-soft); }
  .t247-modal-cell:nth-child(odd) { border-right: 1px solid var(--line-soft); }
  .t247-modal-cell .k { font: 400 9.5px var(--font-mono); letter-spacing: 0.1em; color: var(--text-muted); }
  .t247-modal-cell .v { margin-top: 4px; font: 400 12.5px var(--font-mono); color: #fff; word-break: break-word; }
  .t247-modal-row {
    display: flex; align-items: center; gap: 12px; padding: 10px 20px; border-bottom: 1px solid var(--line-soft);
  }
  .t247-modal-footer {
    display: flex; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--line); background: var(--navy-700); flex: none;
  }
  .t247-btn {
    font: 400 10.5px var(--font-mono); letter-spacing: 0.08em; padding: 9px 16px; cursor: pointer; border: none;
    display: inline-block; text-decoration: none;
  }
  .t247-btn-primary { background: var(--turquoise); color: var(--navy-800); }
  .t247-btn-ghost { background: transparent; border: 1px solid rgba(255,255,255,.2); color: var(--text-dim); }

  @media (max-width: 720px) {
    .t247-modal-overlay { align-items: flex-end; padding: 0; }
    .t247-modal { max-width: 100%; max-height: 88vh; animation: t247SlideUp 0.25s ease; }
    .t247-modal-grid { grid-template-columns: 1fr; }
    .t247-modal-cell:nth-child(odd) { border-right: none; }
  }
  @keyframes t247SlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
`;
